// ═══════════════════════════════════════════════════════════════════════════
// Living Engine — Error Boundary
// packages/@forgetrack/engine/src/ErrorBoundary.tsx
// ═══════════════════════════════════════════════════════════════════════════

import { Component, ReactNode, ErrorInfo } from 'react';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from './tokens';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  label?: string;
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface State { error: Error | null; }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', this.props.label ?? 'unknown', error, info);
    this.props.onError?.(error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);

    return (
      <div style={{
        background: C.surface,
        border: `1px solid ${C.coral}30`,
        borderRadius: 10, padding: 24, margin: '8px 0',
        fontFamily: F.display,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>⚠</span>
          <span style={{
            fontSize: 11, fontFamily: F.mono,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: C.coral, fontWeight: 600,
          }}>
            {this.props.label ?? 'Section'} failed to render
          </span>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          {error.message || 'An unexpected error occurred.'}
        </div>
        <button
          onClick={this.reset}
          style={{
            background: `${C.coral}15`,
            border: `1px solid ${C.coral}40`,
            color: C.coral, fontFamily: F.mono,
            fontSize: 11, letterSpacing: '0.08em',
            padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
          }}
        >
          RETRY
        </button>
      </div>
    );
  }
}
